import type React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    FieldError,
    Input,
    Label,
    Surface,
    TextField,
    Typography,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { BuildCommit } from '@/components/buildCommit';
import { ModeBadge } from '@/components/modeBadge';
import { getNodeTestProps } from '@/utils';
import { DashboardLanguageToggle } from '@/layout/components/DashboardLanguageToggle';
import { DashboardThemeToggle } from '@/layout/components/DashboardThemeToggle';

type EnrollViewProps = {
    token: string;
    setToken: (value: string) => void;
    onSubmit: () => void;
    canSubmit: boolean;
    hasError: boolean;
    /** `pages` key for the failure, chosen by the hook. Null when there is none. */
    errorKey: string | null;
    isPending: boolean;
};

/**
 * Enrollment screen (render-only): a single centred card where the operator
 * pastes the branch API token to bind this terminal to its branch. All state +
 * the exchange call come from `useEnroll`.
 */
export const EnrollView: React.FC<EnrollViewProps> = ({
    token,
    setToken,
    onSubmit,
    canSubmit,
    hasError,
    errorKey,
    isPending,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div
            className="flex min-h-screen flex-col items-center justify-center bg-background px-4 text-foreground"
            {...getNodeTestProps('enroll-screen')}
        >
            <div className="absolute right-4 top-4 flex items-center gap-2">
                <DashboardLanguageToggle
                    className="gap-0"
                    id="enroll-language-toggle"
                    itemIdPrefix="enroll"
                />
                <DashboardThemeToggle
                    className="gap-0"
                    id="enroll-theme-toggle"
                    itemIdPrefix="enroll"
                />
            </div>

            <Surface className="flex w-full max-w-md flex-col gap-6 rounded-3xl p-8">
                <div className="flex flex-col items-center gap-3 text-center">
                    <div className="flex size-12 items-center justify-center rounded-2xl bg-accent text-accent-foreground">
                        <Icon
                            className="size-6"
                            icon="solar:smartphone-2-bold-duotone"
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <Typography type="h4" weight="bold">
                            {t('enroll.title')}
                        </Typography>
                        <Typography color="muted" type="body-sm">
                            {t('enroll.subtitle')}
                        </Typography>
                    </div>
                </div>

                <form
                    className="flex flex-col gap-4"
                    onSubmit={(event) => {
                        event.preventDefault();
                        onSubmit();
                    }}
                >
                    <TextField
                        fullWidth
                        isInvalid={hasError}
                        onChange={setToken}
                        type="password"
                        value={token}
                        variant="secondary"
                    >
                        <Label>{t('enroll.tokenLabel')}</Label>
                        <Input
                            autoFocus
                            className="w-full"
                            {...getNodeTestProps('enroll-token-input')}
                            placeholder={t('enroll.tokenPlaceholder')}
                        />
                        {errorKey ? (
                            <FieldError
                                {...getNodeTestProps('enroll-token-error')}
                            >
                                {t(errorKey)}
                            </FieldError>
                        ) : null}
                    </TextField>

                    <Button
                        className="w-full"
                        {...getNodeTestProps('enroll-submit')}
                        isDisabled={!canSubmit}
                        isPending={isPending}
                        size="lg"
                        type="submit"
                    >
                        <Icon
                            className="size-5"
                            icon="solar:link-bold-duotone"
                        />
                        {t('enroll.submit')}
                    </Button>
                </form>

                <Typography
                    className="text-center"
                    color="muted"
                    type="body-xs"
                >
                    {t('enroll.helper')}
                </Typography>
            </Surface>

            <div className="mt-4 flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
                <BuildCommit />
                <ModeBadge />
            </div>
        </div>
    );
};
